// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { RulesViewerPassword } from '@Molecules/RulesPassword/RulesPassword';
import { THForm } from '@Molecules/THForm/THForm';
import React, { ReactElement } from 'react';
import { PasswordPolicyOutput } from 'thehive-sdk';

import './GlobalUserPassword.scss';

interface GlobalUserPasswordProps {
    passwordRule: PasswordPolicyOutput;
    validator: (str: any) => any;
    hasPassword: boolean;
}

export const GlobalUserPassword = ({ passwordRule, validator, hasPassword }: GlobalUserPasswordProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <>
            <THForm.Item name="setPassword" hidden valuePropName="checked">
                <THSwitch />
            </THForm.Item>

            <THForm.Item label={i('general.password')} shouldUpdate>
                {({ getFieldValue, setFieldsValue }) => {
                    const password = getFieldValue('password') || '';
                    const login = getFieldValue('login') || '';

                    return !getFieldValue('setPassword') ? (
                        <THButton
                            type="link"
                            className="global-user-password__set-button"
                            onClick={() =>
                                setFieldsValue({
                                    setPassword: true,
                                })
                            }
                        >
                            {hasPassword ? 'Edit password' : 'Set a new password'}
                        </THButton>
                    ) : (
                        <THForm.Item
                            name="password"
                            help={
                                passwordRule.enabled && (
                                    <RulesViewerPassword
                                        local={i}
                                        rules={passwordRule}
                                        error={validator(password)}
                                        active
                                    />
                                )
                            }
                            preserve={false}
                            hidden={!getFieldValue('setPassword')}
                            rules={
                                getFieldValue('setPassword')
                                    ? [
                                          {
                                              validator: (_, value) => {
                                                  const validate = validator(value);
                                                  if (
                                                      passwordRule.enabled &&
                                                      ((Array.isArray(validate) && validate.length) ||
                                                          (passwordRule.cannotContainUsername && value === login))
                                                  ) {
                                                      return Promise.reject(validate);
                                                  }
                                                  return Promise.resolve();
                                              },
                                          },
                                      ]
                                    : undefined
                            }
                        >
                            <THPassword borderless placeholder={i('general.password')} />
                        </THForm.Item>
                    );
                }}
            </THForm.Item>
        </>
    );
};
